/**
 * Labstep
 *
 * @module components/EntityImport/Action/Create/Steps/Preview
 * @desc EntityImport Create Step 4: Preview
 */

import { EntityImportActionCreateStepsPreviewWarningModal } from 'labstep-web/components/EntityImport/Action/Create/Steps/Preview/WarningModal';
import { DataGridContextProvider } from 'labstep-web/contexts/grid';
import { useGridEntityImportServiceFromContext } from 'labstep-web/contexts/grid/hook';
import { useModalWizardContext } from 'labstep-web/contexts/modal-wizard/hook';
import { ActionComponent } from 'labstep-web/core/Action/Component';
import { Flex } from 'labstep-web/core/Flex';
import { ModalConfirmation } from 'labstep-web/core/Modal/Confirmation';
import { ModalWizard } from 'labstep-web/core/Modal/Wizard';
import { GridEntityImport } from 'labstep-web/grid/EntityImport';
import { GridEntityImportValidationService } from 'labstep-web/grid/EntityImport/services/grid-entity-import-validation.service';
import { useActiveGroup } from 'labstep-web/hooks/activeGroup';
import type React from 'react';
import { useState } from 'react';
import type { EntityImportActionCreateStepsPreviewProps } from './types';

const getErrorMessage = (count: number, type: 'row' | 'column') =>
  `${count} invalid ${type}${count > 1 ? 's' : ''}`;

export const EntityImportActionCreateStepsPreview: React.FC<
  EntityImportActionCreateStepsPreviewProps
> = ({
  entityName,
  index,
  template,
  columnDefs,
  rowData,
  onContinue,
  create,
}) => {
  const [showOnlyInvalidRows, setShowOnlyInvalidRows] =
    useState(false);

  const [showModalConfirmation, setShowModalConfirmation] =
    useState(false);

  const [isValid, setIsValid] = useState(true);

  const { goBack, setErrorMessages: setError } =
    useModalWizardContext();

  const gridEntityImportService =
    useGridEntityImportServiceFromContext();

  const { activeGroup } = useActiveGroup();

  const [reservedIdentifiersCount, setReservedIdentifiersCount] =
    useState(0);

  const [showWarningModal, setShowWarningModal] = useState(false);

  const [continueHandlers, setContinueHandlers] = useState<{
    setErrorMessages?: (messages: string[]) => void;
    goForward?: VoidFunction;
  }>({});

  const saveData = (
    goForward: VoidFunction,
    setErrorMessages: (messages: string[]) => void,
  ) => {
    if (!gridEntityImportService || !activeGroup) {
      return;
    }

    gridEntityImportService.saveData(
      activeGroup.id,
      entityName,
      create,
      template,
      {
        onSuccess: () => {
          goForward();
          onContinue();
        },
        onFail: () => {
          setErrorMessages([
            'Something went wrong, please try again later',
          ]);
        },
      },
    );
  };

  return (
    <>
      <ModalConfirmation
        open={!!showModalConfirmation}
        header="Warning"
        message="Are you sure you want to go back? All changes will be lost."
        onConfirm={() => {
          setShowModalConfirmation(false);
          goBack();
        }}
        onCancel={() => setShowModalConfirmation(false)}
        onClose={() => setShowModalConfirmation(false)}
      />
      <ModalWizard.Step
        index={index}
        description="Here you can add and modify entries based on the field structure you have defined."
        continueLabel="Import"
        isContinueButtonDisabled={!isValid}
        onContinue={({ setErrorMessages, goForward }) => {
          if (!gridEntityImportService || !activeGroup) {
            return;
          }
          setContinueHandlers({ setErrorMessages, goForward });
          const invalidRowCount =
            gridEntityImportService.getInvalidRowCount();
          if (invalidRowCount > 0) {
            setErrorMessages([
              getErrorMessage(invalidRowCount, 'row'),
            ]);
          } else {
            const reservedIdentifiers =
              GridEntityImportValidationService.getReservedIdentifiers(
                gridEntityImportService.getData(),
                template,
              );
            setReservedIdentifiersCount(reservedIdentifiers.length);

            if (reservedIdentifiers.length === 0) {
              setShowWarningModal(false);
              saveData(goForward, setErrorMessages);
            } else {
              setShowWarningModal(true);
            }
          }
        }}
        onBack={() => setShowModalConfirmation(true)}
        actions={
          <ActionComponent
            type="checkbox"
            elementProps={{
              label: 'Show only invalid rows',
              checked: showOnlyInvalidRows,
            }}
            onClick={(): void => {
              setShowOnlyInvalidRows(!showOnlyInvalidRows);
            }}
          />
        }
      >
        <GridEntityImport
          template={template}
          targetEntityName={entityName}
          rowData={rowData}
          columnDefs={columnDefs}
          showOnlyInvalidRows={showOnlyInvalidRows}
          setErrorMessages={setError}
          setIsValid={setIsValid}
        />
      </ModalWizard.Step>
      {showWarningModal && (
        <EntityImportActionCreateStepsPreviewWarningModal
          count={reservedIdentifiersCount}
          setShowWarningModal={setShowWarningModal}
          onConfirm={() => {
            if (
              continueHandlers.goForward &&
              continueHandlers.setErrorMessages
            ) {
              saveData(
                continueHandlers.goForward,
                continueHandlers.setErrorMessages,
              );
            }
          }}
        />
      )}
    </>
  );
};

export const EntityImportActionCreateStepsPreviewContainer: React.FC<
  EntityImportActionCreateStepsPreviewProps
> = (props) => (
  <DataGridContextProvider>
    <EntityImportActionCreateStepsPreview {...props} />
  </DataGridContextProvider>
);
